import { LitElement, html } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { api } from '../api';
import { errorText, syncLanguage, t } from '../i18n';
import { go } from '../router';
import { rememberFromHash, takeReturn } from '../return-to';
import { hasFreshShared } from '../share-inbox';
import { adoptUser } from '../session';

/** Beta sign-in: username + password, then back to the learner's own home. */
@customElement('signin-view')
export class SigninView extends LitElement {
  @state() private username = '';
  @state() private password = '';
  @state() private busy = false;
  @state() private error = '';

  protected createRenderRoot() { return this; }

  connectedCallback() {
    super.connectedCallback();
    rememberFromHash();
  }

  private submit = async (e: Event) => {
    e.preventDefault();
    if (this.busy) return;
    this.busy = true;
    this.error = '';
    try {
      const { user } = await api.signin(this.username.trim(), this.password);
      // Drafts on this device now follow this account (and only this one).
      adoptUser(user);
      // The account's own interface language, before the first screen draws.
      await syncLanguage();
      // Back to the screen they were going to (once). Else a voice message shared while signed out waits on
      // this device: go and look at it (a share arrives at home, which carries no destination). Else home.
      go(takeReturn(undefined, Date.now(), user.id) ?? ((await hasFreshShared()) ? '/#shared' : '/'), true);
    } catch (err) {
      this.error = errorText(err);
    } finally {
      this.busy = false;
    }
  };

  render() {
    return html`
      <div class="screen narrow account">
        <div class="topbar">
          <img src="/logo-64.png" class="brand-logo" alt="" width="32" height="32" />
          <h1>Eaves</h1>
        </div>
        <img src="/key-art.jpg" class="key-art" alt="" width="840" height="719" />
        <h2>${t('signin.title')}</h2>
        <p class="muted">${t('signin.blurb')}</p>
        <form @submit=${this.submit}>
          <label>${t('signin.username')}
            <input type="text" name="username" autocomplete="username" required
              .value=${this.username} @input=${(e: Event) => (this.username = (e.target as HTMLInputElement).value)} />
          </label>
          <label>${t('signin.password')}
            <input type="password" name="password" autocomplete="current-password" required
              .value=${this.password} @input=${(e: Event) => (this.password = (e.target as HTMLInputElement).value)} />
          </label>
          ${this.error ? html`<p class="error" role="alert">${this.error}</p>` : ''}
          <button type="submit" class="btn primary block" ?disabled=${this.busy}>${this.busy ? t('signin.busy') : t('signin.submit')}</button>
        </form>
        <details class="account-help">
          <summary>${t('signin.accessHelp')}</summary>
          <p class="muted small">${t('signin.invitationHelp')}</p>
          <p class="muted small">${t('signin.recoveryHelp')}</p>
          <p class="small"><a href="https://eaves.world/#waitlist" target="_blank" rel="noreferrer">${t('signin.waitlist')}</a></p>
        </details>
      </div>
    `;
  }
}
